import { useState, useContext, useEffect, useRef } from "react"
import { useQuery } from "@apollo/client"
import { Toast } from "primereact/toast"
import { v4 as uuidv4 } from "uuid"

// Context
import { UserContext } from "../../../context/userContext"

import formStyles from "../../../styles/Form.module.css"
import styles from "../../../styles/Participant-Account.module.css"

// Queries
import { PARTICIPANT_BLOCK } from "./queries"

// Helpers
import validationSchema from "../../../helpers/validationSchema/"
import initialValues from "../../../helpers/initialValues"
import { parseDatesInObj } from "../../../helpers/dateConversion"
import {
  normalizePayload,
  transformPayload,
} from "../../../helpers/dateNormalization"

// Comps
import { LoaderMedium } from "../../../components/Loaders"
import { Formik, Form as FormWrapper } from "formik"
import { Button } from "primereact/button"
import FieldBuilder from "./FieldBuilder"

// Helpers
import {
  nameInitials,
  AccountBasicInfoFields,
  CurrentPasswordField,
  NewPasswordField,
} from "../Utils"
import ProfileAvatar from "../../../components/ProfileAvatar"
import { LogError } from "../../../helpers/logger"
import { callCloudFunction } from "../../../helpers/cloudFunctionRequests"

export default function AccountEdit() {
  const userCtx = useContext(UserContext)
  const toast = useRef(null)
  const [fetchData, setFetchData] = useState(undefined)
  const [loading, setLoading] = useState(false)
  const [formId] = useState(() => uuidv4())

  const participant_id = userCtx.participant_id

  const { refetch: refetchParticipantBlock } = useQuery(PARTICIPANT_BLOCK, {
    fetchPolicy: "network-only",
    variables: {
      participant_id,
      // When loading participant account info, always load from the first organization
      organization_id: userCtx.participant_organizations?.[0]?.organization_id,
      enterprise_id: userCtx.participant_organizations?.[0]?.enterprise_id,
      idBlock: "participant",
    },
    skip: true,
  })

  function ProcessBlockData(data) {
    let _fetchData = structuredClone(data)

    if (_fetchData.blockSaved?.data && _fetchData.block) {
      try {
        const parsedBlockSavedData = JSON.parse(_fetchData.blockSaved.data)
        const rawValues = parsedBlockSavedData?.values ?? {}
        const transformedValues =
          transformPayload(rawValues, [_fetchData.block]) ?? rawValues

        _fetchData.blockSavedTransformed = {
          ...parsedBlockSavedData,
          values: transformedValues,
        }
      } catch (error) {
        LogError("Error transforming block data:", error)
      }
    }

    setFetchData(_fetchData)
  }

  useEffect(() => {
    setLoading(true)
    refetchParticipantBlock()
      .then(({ data }) => {
        ProcessBlockData(data)
      })
      .catch((error) => {
        LogError("Error fetching participant data:", error)
      })
      .finally(() => {
        setLoading(false)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (loading || !fetchData) return <LoaderMedium />

  const dataStored = fetchData.blockSavedTransformed
    ? fetchData.blockSavedTransformed
    : fetchData.blockSaved
      ? JSON.parse(fetchData.blockSaved.data)
      : {}

  const formValues = {
    ...initialValues(fetchData?.block),
    ...parseDatesInObj(dataStored?.values),
    participant_current_password: "",
    participant_account_password: "",
  }

  const basicInfoCard = fetchData.block.fields.filter((field) =>
    AccountBasicInfoFields.includes(field.idField),
  )

  const renderProfilePictureUploader = () => {
    const profileImageField = fetchData.block.fields.find(
      (f) => f.idField === "participant_profile_image",
    )
    return (
      <FieldBuilder
        field={{ ...profileImageField }}
        block={"participant"}
        organization_id={
          userCtx.participant_organizations?.[0]?.organization_id
        }
        enterprise_id={userCtx.participant_organizations?.[0]?.enterprise_id}
      />
    )
  }

  const renderPasswordUpdate = () => {
    return (
      <>
        <FieldBuilder
          field={{ ...CurrentPasswordField }}
          block={"participant"}
          organization_id={
            userCtx.participant_organizations?.[0]?.organization_id
          }
          enterprise_id={userCtx.participant_organizations?.[0]?.enterprise_id}
        />
        <FieldBuilder
          field={{ ...NewPasswordField }}
          block={"participant"}
          organization_id={
            userCtx.participant_organizations?.[0]?.organization_id
          }
          enterprise_id={userCtx.participant_organizations?.[0]?.enterprise_id}
        />
      </>
    )
  }

  const handleSubmit = async (values, actions) => {
    setLoading(true)

    try {
      const normalizedValues =
        normalizePayload(values, [fetchData.block]) ?? values

      // Save account info across all participant organizations in parallel.
      const savePromises = userCtx.participant_organizations.map(
        (organization) => {
          const contextFormId = `${formId}-orgId-${organization.organization_id}` // Unique form ID for each organization to prevent conflicts in staged submissions
          const postBody = {
            idDoc: dataStored?.idDoc ?? undefined,
            organization_alias: organization.organization_alias,
            organization_id: organization.organization_id,
            enterprise_id: organization.enterprise_id,
            enterprise_alias: organization.enterprise_alias,
            participant_alias: userCtx.participant_alias,
            participant_membership_id: organization.participant_membership_id,
            participant_id,
            staff_alias: 0,
            staff_id: 0,
            form_id: contextFormId,
            block_name: "participant",
            values: normalizedValues,
          }

          return callCloudFunction(
            "blockSubmitParticipant",
            postBody,
            userCtx.tokens.getMainToken(),
          ).then(async (response) => {
            if (response.status === 200) {
              try {
                // Clean up staged submission after successful submission/edit
                await callCloudFunction(
                  "deleteStagedFormSubmission",
                  { form_id: contextFormId },
                  userCtx.tokens.getMainToken(),
                )
              } catch (error) {
                LogError("Error processing block submission response:", error)
              }
            }

            return { response, postBody }
          })
        },
      )

      // Run all save operations in parallel and wait for them to complete.
      const saveResults = await Promise.allSettled(savePromises)

      // Check results of all save operations to ensure they were successful.
      const failedSaves = saveResults.filter((result) => {
        if (result.status === "rejected") return true
        return result.value?.response?.status !== 200
      })
      // If any save operation failed, throw an error to trigger the catch block.
      if (failedSaves.length > 0) {
        throw new Error(
          "We couldn't save your account info across all organizations.",
        )
      }

      // Refetch participant data
      userCtx.refetchParticipantData()
      const { data } = await refetchParticipantBlock()
      ProcessBlockData(data)
    } catch (error) {
      LogError("Error saving participant data:", error)
      toast.current.show({
        severity: "error",
        summary: "Unable to save account info",
        detail: (
          <>
            <p>
              The following error occurred while trying to save your account
              info: {error.message}
            </p>
            <br />
            <p>If this error persists, please contact support.</p>
          </>
        ),
        life: 10000,
      })
    } finally {
      setLoading(false)
      actions.setSubmitting(false)
    }
  }

  const CustomAvatar = () => {
    const initials = nameInitials(
      formValues.participant_name_first,
      formValues.participant_name_last,
    )
    return <ProfileAvatar nameInitials={initials} showRemovebtn />
  }

  const validation = () => {
    let block = { fields: [...basicInfoCard] }
    block.fields.push(CurrentPasswordField)
    block.fields.push(NewPasswordField)
    return validationSchema(block)
  }

  return (
    <div>
      <Toast ref={toast} />
      <h3 style={{ marginBottom: "30px" }}>Account</h3>
      <Formik
        initialValues={formValues}
        validationSchema={validation()}
        onSubmit={async (values, actions) => {
          await handleSubmit(values, actions)
        }}
      >
        {({ resetForm, dirty }) => (
          <FormWrapper className="" key={"participant_account"}>
            <div className={styles.profile_image_card}>
              <div className={styles.profile_image_content}>
                <CustomAvatar />
                <div>
                  <p
                    style={{ fontWeight: "600", color: "#383838" }}
                  >{`${formValues.participant_name_first} ${formValues.participant_name_last}`}</p>
                  <p style={{ color: "#999999" }}>
                    {formValues.participant_email_account}
                  </p>
                </div>
              </div>
              <div
                className={
                  styles.profile_image_button +
                  " " +
                  "profileImageUpload_Container"
                }
              >
                {renderProfilePictureUploader()}
              </div>
            </div>
            <div className={styles.custom_card}>
              <h4 style={{ marginBottom: "5px", fontWeight: "bold" }}>
                Basic Info
              </h4>
              <div className={formStyles.gridContainer}>
                {basicInfoCard.map((field, index) => {
                  const _field = { ...field }
                  return (
                    <FieldBuilder
                      key={_field.idField}
                      field={_field}
                      index={index}
                      block={"participant"}
                      organization_id={
                        userCtx.participant_organizations?.[0]?.organization_id
                      }
                      enterprise_id={
                        userCtx.participant_organizations?.[0]?.enterprise_id
                      }
                    />
                  )
                })}
              </div>
            </div>
            <div className={styles.custom_card}>
              <h4 style={{ marginBottom: "5px", fontWeight: "bold" }}>
                Change Password
              </h4>
              <div className={formStyles.gridContainer}>
                {renderPasswordUpdate()}
              </div>
            </div>
            <div
              style={{
                display: "flex",
                gap: "20px",
                height: "48px",
                width: "100%",
                justifyContent: "flex-end",
              }}
            >
              <Button
                label="Cancel"
                type="button"
                onClick={() => resetForm()}
                className="p-button-rounded p-button-secondary"
                style={{ minWidth: "80px" }}
              />
              <Button
                type="submit"
                label="Save"
                className="p-button-rounded"
                style={{ minWidth: "80px" }}
                disabled={!dirty}
              />
            </div>
          </FormWrapper>
        )}
      </Formik>
    </div>
  )
}
